import * as React from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { TabsWithUrl, TabMode } from '@common/react/components/UI/TabsWithUrl/TabsWithUrl';

import { PriceType } from '@app/objects/Price';
import { ServiceType, getServiceTypeKey } from '@app/objects/Pet';
import { UserRole } from '@app/objects/User';
import { ApplicationState } from '@app/store';
import { useServiceTypes, ServiceTypeOption } from '@app/hooks/Crematory/useServiceTypes';

interface OwnProps {
	urlPrefix?: string;
	children: (data: SingleTableOptions) => React.ReactElement;
}

export interface SingleTableOptions {
	price: PriceType;
	service: ServiceType;
	title: string;
}

interface SingleTableProps {
	name: string;
	urlPrefix?: string;
	priceType: PriceType;
	children: (data: SingleTableOptions) => JSX.Element;
	fullWidth?: boolean;
}

export function useTableOptions(priceType: PriceType): Array<SingleTableOptions> {
	const options = useServiceTypes();

	return options.map((option: ServiceTypeOption) => ({
		price: priceType,
		service: option.value,
		title: option.label,
	}));
}

export const SingleTable: React.FC<SingleTableProps> = ({
	urlPrefix, name, priceType, children, fullWidth,
}) => {
	const options = useTableOptions(priceType);

	const items = React.useMemo(() => options.map((option) => ({
		key: getServiceTypeKey(option.service),
		label: option.title,
		children: children(option),
	})), [options, children]);

	if (!options.length) return null;

	return (
		<div className={clsx('row-tabs', fullWidth ? 'card__body' : 'col-sm-6')}>
			<div className="site-subheadline" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
				<h1>{name}</h1>
			</div>
			<TabsWithUrl
				mode={TabMode.Query}
				tabsUrlKey={urlPrefix ? `${urlPrefix}-${name.toLowerCase()}` : name.toLowerCase()}
				defaultKey={getServiceTypeKey(options[0]?.service)}
				items={items}
			/>
		</div>
	);
};

export const CrematoryTables: React.FC<OwnProps> = ({ children, urlPrefix }: OwnProps) => {
	const role = useSelector((state: ApplicationState) => state.login.user?.role);

	return (
		<div className={clsx('row prices-tables', role === UserRole.Crematory && 'table--margin')}>
			<SingleTable name="Wholesale" urlPrefix={urlPrefix} priceType={PriceType.Wholesale}>{children}</SingleTable>
			<SingleTable name="Retail" urlPrefix={urlPrefix} priceType={PriceType.Retail}>{children}</SingleTable>
		</div>
	);
};
